import React, { memo } from 'react'
import clsx from 'clsx'

interface YearQuarterSelectorProps {
  selectedYear: number
  selectedQuarter: 'all' | 'q1' | 'q2' | 'q3' | 'q4'
  onYearChange: (year: number) => void
  onQuarterChange: (quarter: 'all' | 'q1' | 'q2' | 'q3' | 'q4') => void
  disabled?: boolean
}

const YearQuarterSelector: React.FC<YearQuarterSelectorProps> = memo(({
  selectedYear,
  selectedQuarter,
  onYearChange,
  onQuarterChange,
  disabled = false
}) => {
  // Generate past 5 years
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i)

  const quarters = [
    { value: 'all', label: 'All Year' },
    { value: 'q1', label: '1st Quarter' },
    { value: 'q2', label: '2nd Quarter' },
    { value: 'q3', label: '3rd Quarter' },
    { value: 'q4', label: '4th Quarter' }
  ] as const

  return (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
      {/* Year Dropdown */}
      <div className="flex items-center gap-2">
        <label htmlFor="year-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">
          Year:
        </label>
        <select
          id="year-select"
          value={selectedYear}
          onChange={(e) => !disabled && onYearChange(parseInt(e.target.value))}
          disabled={disabled}
          className={clsx(
            "rounded-md border border-gray-300 bg-white py-1.5 px-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500",
            disabled && "opacity-50 cursor-not-allowed bg-gray-50"
          )}
        >
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Quarter Pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          Period:
        </span>
        <div className="flex gap-1">
          {quarters.map(quarter => (
            <button
              key={quarter.value}
              onClick={() => !disabled && onQuarterChange(quarter.value)}
              disabled={disabled}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-150',
                disabled && 'opacity-50 cursor-not-allowed',
                selectedQuarter === quarter.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                disabled && selectedQuarter === quarter.value && 'bg-indigo-400',
                disabled && selectedQuarter !== quarter.value && 'bg-gray-50 text-gray-400'
              )}
            >
              {quarter.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

YearQuarterSelector.displayName = 'YearQuarterSelector'

export default YearQuarterSelector